import React, { createContext, useContext, useState, useEffect } from 'react';
import dataCache from '../utils/dataCache';
import {
  readExcelFile,
  processDeliveryData,
  processMDMData,
  aggregateByPlatform,
  aggregateByStore,
  getOrderTrends,
  getTopStores,
  getMaterialGroupPerformance,
  getDailyQuantityPicked,
  getWeeklyQuantityByStore,
  getDailyPickedByPlatform,
  getFillRateOverTime,
  getAverageLinesByWeekdayPlatform,
  getTopItemsByFillRate,
  getWeeklyTrends
} from '../utils/dataProcessing';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState('');
  
  // Core data
  const [processedData, setProcessedData] = useState([]);
  const [platformMapping, setPlatformMapping] = useState({});
  const [storeNameMapping, setStoreNameMapping] = useState({});
  
  // Aggregated data
  const [platformStats, setPlatformStats] = useState({});
  const [storeStats, setStoreStats] = useState({});
  const [orderTrends, setOrderTrends] = useState([]);
  const [topStores, setTopStores] = useState([]);
  const [materialGroups, setMaterialGroups] = useState([]);
  
  // Chart data
  const [dailyQuantityPicked, setDailyQuantityPicked] = useState([]);
  const [weeklyStoreData, setWeeklyStoreData] = useState({});
  const [dailyPlatformData, setDailyPlatformData] = useState([]);
  const [fillRateData, setFillRateData] = useState({ fillRateData: [], trendData: [] });
  const [heatmapData, setHeatmapData] = useState({ heatmapData: [], weekdays: [], platforms: [] });
  const [topItemsByFillRate, setTopItemsByFillRate] = useState([]);
  const [weeklyTrends, setWeeklyTrends] = useState([]);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if we have cached processed data
      const cachedProcessedData = dataCache.getProcessedData();
      const cachedPlatformMapping = dataCache.getPlatformMapping();
      const cachedStoreNameMapping = dataCache.getStoreNameMapping();
      
      let processedDeliveryData, platformMap, storeNameMap;
      
      if (cachedProcessedData && cachedPlatformMapping && cachedStoreNameMapping) {
        console.log('Using cached processed data');
        processedDeliveryData = cachedProcessedData;
        platformMap = cachedPlatformMapping;
        storeNameMap = cachedStoreNameMapping;
      } else {
        // Load Excel files (with caching)
        setLoadingProgress('Loading Excel files...');
        const { atlData, obdData, newData, mdmData } = await dataCache.getExcelData(readExcelFile);
        
        // Process data
        setLoadingProgress('Processing delivery data...');
        processedDeliveryData = processDeliveryData(atlData, obdData, newData);
        const mdmResult = processMDMData(mdmData);
        platformMap = mdmResult.platformMapping;
        storeNameMap = mdmResult.storeNameMapping;
        
        // Cache processed data
        dataCache.setProcessedData(processedDeliveryData);
        dataCache.setPlatformMapping(platformMap);
        dataCache.setStoreNameMapping(storeNameMap);
      }
      
      // Store core data
      setProcessedData(processedDeliveryData);
      setPlatformMapping(platformMap);
      setStoreNameMapping(storeNameMap);
      
      // Process aggregations
      setLoadingProgress('Calculating statistics...');
      
      // Platform and store stats
      const platformAggregates = aggregateByPlatform(processedDeliveryData, platformMap);
      setPlatformStats(platformAggregates);
      
      const storeAggregates = aggregateByStore(processedDeliveryData);
      setStoreStats(storeAggregates);
      
      // Trends and top lists
      const trends = getOrderTrends(processedDeliveryData);
      setOrderTrends(trends);
      
      const topStoresList = getTopStores(storeAggregates, platformMap, storeNameMap, 10);
      setTopStores(topStoresList);
      
      const materialGroupPerf = getMaterialGroupPerformance(processedDeliveryData);
      setMaterialGroups(materialGroupPerf.sort((a, b) => b.orderCount - a.orderCount).slice(0, 10));
      
      // Chart data
      setLoadingProgress('Preparing visualizations...');
      
      const dailyQtyPicked = getDailyQuantityPicked(processedDeliveryData);
      setDailyQuantityPicked(dailyQtyPicked);
      
      const weeklyByStore = getWeeklyQuantityByStore(processedDeliveryData);
      setWeeklyStoreData(weeklyByStore);
      
      const dailyByPlatform = getDailyPickedByPlatform(processedDeliveryData, platformMap);
      setDailyPlatformData(dailyByPlatform);
      
      const fillRateOverTime = getFillRateOverTime(processedDeliveryData);
      setFillRateData(fillRateOverTime);
      
      const weekdayHeatmap = getAverageLinesByWeekdayPlatform(processedDeliveryData, platformMap);
      setHeatmapData(weekdayHeatmap);
      
      const topItems = getTopItemsByFillRate(processedDeliveryData, 20);
      setTopItemsByFillRate(topItems);
      
      const weeklyTrendsData = getWeeklyTrends(processedDeliveryData);
      setWeeklyTrends(weeklyTrendsData);
      
      setLoadingProgress('');
      setLoading(false);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const refreshData = () => {
    dataCache.clearCache();
    loadAllData();
  };

  const value = {
    // Status
    loading,
    error,
    loadingProgress,
    refreshData,
    
    // Core data
    processedData,
    platformMapping,
    storeNameMapping,
    
    // Aggregated data
    platformStats,
    storeStats,
    orderTrends,
    topStores,
    materialGroups,
    
    // Chart data
    dailyQuantityPicked,
    weeklyStoreData,
    dailyPlatformData,
    fillRateData,
    heatmapData,
    topItemsByFillRate,
    weeklyTrends
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};